import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import MDButton from "components/MDButton";
import PropTypes from "prop-types";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { useTranslation } from "react-i18next";
import { fieldStyles } from "services/utils";
import { getFunctionalByKpiAssignment } from "services/api";

const frequencyOptions = ["Monthly", "Quarterly", "Semi Annual", "Annual"];
const perspectives = [
  "Strategic Initiatives",
  "Strategic Projects",
  "Finance Viability",
  "Internal Process",
  "Internal StakeHolder",
  "External StakeHolder",
  "Compliance",
  "Audit",
];

const accs = ["Prime", "Shared", "Contributory", "Remote"];

const AddKpiAssignmentDialog = ({
  open,
  onClose,
  onSubmit,
  newKpi,
  setNewKpi,
  kpiNames,
  employees,
  filteredKpis, // Pass filteredKpis as a prop
  isEditMode,
  setIsEditMode, // Allow updating edit mode
  setSelectedKpiId, // Allow setting the selected KPI ID
  onDelete,
  functionalAssessors,
  setFunctionalAssessors,
  selectedKpiId,
  jobs,
  evidenceProviders,
  setEvidenceProviders,
}) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({});

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const selectedKpi = filteredKpis.find((kpi) => kpi.kpiAssignmentsId === selectedKpiId);

  const [primaryAssessorType, setPrimaryAssessorType] = useState(
    newKpi.primaryAssessorUId != null ? "chooseAssessor" : "directManager"
  );

  const [secondaryAssessorType, setSecondaryAssessorType] = useState(
    newKpi.secondaryAssessorUId != null ? "chooseAssessor" : "indirectManager"
  );
  const [funcLoading, setFuncLoading] = useState(false);
  const [evidenceProviderLoading, setEvidenceProviderLoading] = useState(false);

  useEffect(() => {
    setPrimaryAssessorType(newKpi?.primaryAssessorUId != null ? "chooseAssessor" : "directManager");
    setSecondaryAssessorType(
      newKpi?.secondaryAssessorUId != null ? "chooseAssessor" : "indirectManager"
    );

    const fetchFunctionalAssessors = async () => {
      if (!open) {
        return;
      }
      setFuncLoading(true);

      if (selectedKpiId) {
        // Ensure newKpi exists and has an ID
        const data = await getFunctionalByKpiAssignment(selectedKpiId);

        const employees = data.map((x) => x.employeeUId);
        setFunctionalAssessors(employees);
      } else {
        setFunctionalAssessors([]); // Reset if newKpi is null
      }
      setFuncLoading(false);
    };

    fetchFunctionalAssessors();
  }, [open]);

  const validate = () => {
    const newErrors = {};

    if (!newKpi.kpiNameId) {
      newErrors.kpiNameId = t("admin.scorecard.dialog.kpiNameError");
    }

    if (!newKpi.weight || newKpi.weight <= 0) {
      newErrors.weight = t("admin.scorecard.dialog.weightError");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSubmit();
    }
  };
  const handleKpiSelection = (e, newValue) => {
    if (!newValue) return;

    const existingKpi = filteredKpis.find((kpi) => kpi.kpiNameId === newValue.kpiNameId);

    if (existingKpi) {
      // Switch to edit mode if KPI already exists
    } else {
      // Stay in add mode for new KPI
      setIsEditMode(false);
      setSelectedKpiId(null);
      setNewKpi({
        ...newKpi,
        kpiNameId: newValue.kpiNameId,
        perspectiveId: newValue.perspectiveId || null,
        kpiCode: newValue.kpiCode || "",
        weight: null,
        target: null,
        primaryAssessorUId: null,
        frequency: "",
        source: "",
        from: new Date(),
        to: null,
        accountability: "",
        primaryPercentage: newValue.kpiPercentage || 100,
        description: "",
      });
    }
  };

  const sortedEmployees = useMemo(() => {
    return [...employees].sort((a, b) => {
      const jobA = jobs.find((j) => j.jobId === a.jobId)?.jobName || "";
      const jobB = jobs.find((j) => j.jobId === b.jobId)?.jobName || "";
      return jobA.localeCompare(jobB);
    });
  }, [employees, jobs]);

  const filter = createFilterOptions({
    stringify: (option) => {
      const job = jobs.find((j) => j.jobId === option.jobId);
      return `${option.name} ${job?.jobName || ""}`;
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: darkMode ? "#333" : "#fff", // Changes only the dialog background
          color: darkMode ? "#fff" : "#000", // Ensures text color is visible
          borderRadius: 2, // Optional: Smooth rounded corners
          padding: 2, // Optional: Adds some spacing inside
        },
      }}
      fullWidth
    >
      <DialogTitle>
        {isEditMode ? t("admin.scorecard.dialog.editTitle") : t("admin.scorecard.dialog.addTitle")}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* KPI Name Dropdown */}
          <Grid item xs={12}>
            <Autocomplete
              options={kpiNames.filter(
                (kpi) =>
                  kpi.kpiNameId === newKpi.kpiNameId || // allow the selected one
                  !filteredKpis.some((assignedKpi) => assignedKpi.kpiNameId === kpi.kpiNameId)
              )}
              getOptionLabel={(option) => option.kpiName}
              value={kpiNames.find((kpi) => kpi.kpiNameId === newKpi.kpiNameId) || null}
              onChange={handleKpiSelection}
              isOptionEqualToValue={(option, value) => option.kpiNameId === value.kpiNameId}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("admin.scorecard.dialog.selectKpi") + " *"}
                  fullWidth
                  error={!!errors.kpiNameId}
                  helperText={errors.kpiNameId}
                />
              )}
            />
          </Grid>
          {/* KPI Descripton */}
          <Grid item xs={12}>
            <TextField
              label={t("admin.scorecard.dialog.description")}
              // value={kpiNames.find((kpi) => kpi.kpiNameId === newKpi.kpiNameId)?.description || ""}
              value={newKpi.description || ""}
              onChange={(e) => setNewKpi({ ...newKpi, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
          </Grid>

          {/* KPI Code */}
          <Grid item xs={12}>
            <TextField
              label={t("admin.scorecard.dialog.kpiCode")}
              value={newKpi.kpiCode}
              onChange={(e) => setNewKpi({ ...newKpi, kpiCode: e.target.value })}
              type="number"
              fullWidth
            />
          </Grid>

          {/* ACCs Dropdown */}
          <Grid item xs={12}>
            <TextField
              select
              label={t("admin.scorecard.dialog.accountability")}
              value={newKpi.accountability || ""} // Ensure it has a default value
              onChange={(e) => setNewKpi({ ...newKpi, accountability: e.target.value })}
              fullWidth
              sx={fieldStyles}
            >
              {accs.map((acc) => (
                <MenuItem key={acc} value={acc}>
                  {acc} {/* Assuming acc has id & name */}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Percentage Inputs */}
          <Grid item xs={6}>
            <TextField
              type="number"
              label={t("admin.scorecard.dialog.primaryPercentage")}
              value={newKpi.primaryPercentage || 100}
              onChange={(e) => {
                const value = Math.min(Math.max(parseInt(e.target.value) || 0, 0), 100);
                setNewKpi({
                  ...newKpi,
                  primaryPercentage: value,
                });
              }}
              inputProps={{
                min: 0,
                max: 100,
                step: 1,
              }}
              fullWidth
              sx={fieldStyles}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              type="number"
              label={t("admin.scorecard.dialog.secondaryPercentage")}
              value={newKpi.primaryPercentage ? 100 - newKpi.primaryPercentage : 0}
              onChange={(e) => {
                const value = Math.min(Math.max(parseInt(e.target.value) || 0, 0), 100);
                setNewKpi({
                  ...newKpi,
                  primaryPercentage: 100 - value,
                });
              }}
              inputProps={{
                min: 0,
                max: 100,
                step: 1,
              }}
              fullWidth
              sx={fieldStyles}
            />
          </Grid>

          <Grid item xs={12}>
            <MDTypography variant="subtitle1" fontWeight="bold" gutterBottom>
              {t("admin.scorecard.dialog.primaryAssessor")}
            </MDTypography>
            <FormControl component="fieldset">
              <RadioGroup
                row
                value={primaryAssessorType}
                onChange={(e) => {
                  const value = e.target.value;
                  setPrimaryAssessorType(value);
                  setNewKpi({
                    ...newKpi,
                    primaryAssessorUId:
                      value === "directManager" ? null : newKpi.primaryAssessorUId,
                  });
                }}
              >
                <FormControlLabel
                  value="directManager"
                  control={<Radio />}
                  label={t("admin.scorecard.dialog.directManager")}
                />
                <FormControlLabel
                  value="chooseAssessor"
                  control={<Radio />}
                  label={t("admin.scorecard.dialog.chooseAssessor")}
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {/* Show Autocomplete only if "Choose Assessor" is selected */}
          {primaryAssessorType === "chooseAssessor" && (
            <Grid item xs={12}>
              <Autocomplete
                options={employees}
                getOptionLabel={(option) => option.name}
                value={employees.find((e) => e.id === newKpi.primaryAssessorUId) || null}
                onChange={(e, newValue) =>
                  setNewKpi({
                    ...newKpi,
                    primaryAssessorUId: newValue?.id || null,
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("admin.scorecard.dialog.assessorSelection")}
                    fullWidth
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <MDTypography variant="subtitle1" fontWeight="bold" gutterBottom>
              {t("admin.scorecard.dialog.secondaryAssessor")}
            </MDTypography>
            <FormControl component="fieldset">
              <RadioGroup
                row
                value={secondaryAssessorType}
                onChange={(e) => {
                  const value = e.target.value;
                  setSecondaryAssessorType(value);
                  setNewKpi({
                    ...newKpi,
                    secondaryAssessorUId:
                      value === "indirectManager" ? null : newKpi.secondaryAssessorUId,
                  });
                }}
              >
                <FormControlLabel
                  value="indirectManager"
                  control={<Radio />}
                  label={t("admin.scorecard.dialog.indirectManager")}
                />
                <FormControlLabel
                  value="chooseAssessor"
                  control={<Radio />}
                  label={t("admin.scorecard.dialog.chooseAssessor")}
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {/* Show Autocomplete only if "Choose Assessor" is selected */}
          {secondaryAssessorType === "chooseAssessor" && (
            <Grid item xs={12}>
              <Autocomplete
                options={employees}
                getOptionLabel={(option) => option.name}
                value={employees.find((e) => e.id === newKpi.secondaryAssessorUId) || null}
                onChange={(e, newValue) =>
                  setNewKpi({
                    ...newKpi,
                    secondaryAssessorUId: newValue?.id || null,
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("admin.scorecard.dialog.assessorSelection")}
                    fullWidth
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            {funcLoading ? (
              <Box display="flex" justifyContent="center" p={1}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <Autocomplete
                multiple
                options={sortedEmployees}
                getOptionLabel={(option) => `${option.name}`}
                groupBy={(option) => {
                  const job = jobs.find((j) => j.jobId === option.jobId);
                  return job?.jobName || "Unknown Job";
                }}
                filterOptions={filter}
                value={employees.filter((emp) => functionalAssessors.includes(emp.id))}
                onChange={(event, newValue) => {
                  const selectedIds = newValue.map((emp) => emp.id);
                  setFunctionalAssessors(selectedIds);
                }}
                loading={funcLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("admin.inventory.functionalAssessors")}
                    placeholder="Select employees by job"
                  />
                )}
              />
            )}
          </Grid>

          {/*Evidence Providers*/}
          <Grid item xs={12}>
            {evidenceProviderLoading ? (
              <Box display="flex" justifyContent="center" p={1}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <Autocomplete
                multiple
                options={sortedEmployees}
                getOptionLabel={(option) => `${option.name}`}
                groupBy={(option) => {
                  const job = jobs.find((j) => j.jobId === option.jobId);
                  return job?.jobName || "Unknown Job";
                }}
                filterOptions={filter}
                value={employees.filter((emp) => (evidenceProviders || []).includes(emp.id))}
                onChange={(event, newValue) => {
                  const uuids = newValue.map((emp) => emp.id);
                  setEvidenceProviders(uuids);
                }}
                loading={funcLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("admin.inventory.evidenceProviders")}
                    placeholder="Select employees by job"
                  />
                )}
              />
            )}
          </Grid>

          {/* Weight */}
          <Grid item xs={12}>
            <TextField
              label={t("admin.scorecard.dialog.weight") + " *"}
              type="number"
              value={newKpi.weight || ""}
              onChange={(e) => setNewKpi({ ...newKpi, weight: parseFloat(e.target.value) })}
              fullWidth
              error={!!errors.weight}
              helperText={errors.weight}
            />
          </Grid>

          {/* Target */}
          <Grid item xs={12}>
            <TextField
              label={t("admin.scorecard.dialog.target")}
              type="number"
              value={newKpi.target || ""}
              onChange={(e) => setNewKpi({ ...newKpi, target: parseFloat(e.target.value) })}
              fullWidth
            />
          </Grid>

          {/* Frequency Dropdown */}
          <Grid item xs={12}>
            <FormControl fullWidth sx={fieldStyles}>
              <InputLabel>{t("admin.scorecard.dialog.frequency")}</InputLabel>
              <Select
                value={newKpi.frequency}
                onChange={(e) => setNewKpi({ ...newKpi, frequency: e.target.value })}
                label="Frequency"
                sx={{ minHeight: 30 }}
              >
                {frequencyOptions.map((freq) => (
                  <MenuItem key={freq} value={freq}>
                    {freq}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Source */}
          <Grid item xs={12}>
            <TextField
              label={t("admin.inventory.source")}
              value={newKpi.source}
              onChange={(e) => setNewKpi({ ...newKpi, source: e.target.value })}
              fullWidth
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose}>{t("admin.scorecard.dialog.cancel")}</MDButton>

        {isEditMode && (
          <Button
            onClick={onDelete}
            color="error"
            sx={{
              color: "red",
              "&:hover": { backgroundColor: "red", color: "#fff" },
            }}
          >
            {t("admin.scorecard.dialog.delete")}
          </Button>
        )}

        <Button
          onClick={handleSubmit}
          color="primary"
          sx={{
            "&:hover": { backgroundColor: "blue", color: "#fff" },
          }}
        >
          {isEditMode ? t("admin.scorecard.dialog.update") : t("admin.scorecard.dialog.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddKpiAssignmentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  filteredKpis: PropTypes.array.isRequired,
  setIsEditMode: PropTypes.func.isRequired,
  setSelectedKpiId: PropTypes.func.isRequired,
  newKpi: PropTypes.object.isRequired,
  setNewKpi: PropTypes.func.isRequired,
  kpiNames: PropTypes.arrayOf(
    PropTypes.shape({
      kpiNameId: PropTypes.number.isRequired,
      kpiName: PropTypes.string.isRequired,
    })
  ).isRequired,
  employees: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  isEditMode: PropTypes.bool, // Add isEditMode prop type
  onDelete: PropTypes.func, // Add onDelete prop type
  functionalAssessors: PropTypes.array.isRequired,
  setFunctionalAssessors: PropTypes.func.isRequired,
  selectedKpiId: PropTypes.number.isRequired,
  jobs: PropTypes.array.isRequired,
  evidenceProviders: PropTypes.array.isRequired,
  setEvidenceProviders: PropTypes.func.isRequired,
};

export default AddKpiAssignmentDialog;
